import type { Task } from '@dyspatch/core';
import { holdNeedsAPerson } from '@/lib/fleet';

/**
 * The account page's glance at "your steps" (`WorkSection.tsx`), ranked and
 * capped in a module of its own so a test can pin the ordering without
 * standing up the whole page.
 *
 * The account page is not a second copy of the Tasks board: it answers "what
 * is waiting on me", not "list every step". Printing whatever order the API
 * happened to return - the old behaviour - meant a member with thirty queued
 * steps and one offline fleet saw thirty rows that were all, in effect, the
 * same row, while the two or three steps that actually needed a decision from
 * them were buried wherever the sort put them. So the list here is ranked by
 * what needs the reader before it is cut down to a size a glance can hold.
 */

/**
 * Four tiers, worst first. A step only ever lands in the first one that
 * applies - `review` outranks a hold, a hold outranks a pause note it does
 * not have, and so on - because a row is one thing to a reader, not a
 * combination of all the things that happen to be true about it.
 *
 * - Waiting on a person to review it (`status === 'review'`): the one state
 *   with a human decision already queued behind it.
 * - Paused: a person stopped this step on purpose and it does not move again
 *   without one saying so.
 * - Held for the one reason that does not clear itself
 *   (`holdNeedsAPerson`, see `lib/fleet.ts`): every other hold is the
 *   scheduler waiting on the world, which is not a person's problem to solve.
 * - Everything else: running, freshly queued, failed with retries left,
 *   draft. The fleet or the clock is already moving these on its own, so they
 *   fill out the glance only once the tiers above are exhausted.
 */
function tierOf(task: Task): number {
  if (task.status === 'review') return 0;
  if (task.pausedAt) return 1;
  if (holdNeedsAPerson(task)) return 2;
  return 3;
}

/**
 * How long a step has been sitting in whatever it is that makes it belong to
 * its tier, so the oldest instance of the worst tier leads the list rather
 * than whichever one the last poll happened to return first. The same
 * "oldest first" convention `taskPageGroups`'s gate group and `planApprovals`
 * already sort by, including reading `reviewEnteredAt` over `updatedAt` for a
 * step in review - `updatedAt` moves on this task for unrelated reasons (a PR
 * pushed for a linked task, say) and would reorder the queue for a reason a
 * reader can't see.
 */
function sortKey(task: Task): number {
  const at = task.status === 'review' ? (task.reviewEnteredAt ?? task.updatedAt) : task.updatedAt;
  const parsed = Date.parse(at);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Every step, in the order the glance would show them if it had no cap. */
export function rankStepsForGlance(tasks: readonly Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    const byTier = tierOf(a) - tierOf(b);
    return byTier !== 0 ? byTier : sortKey(a) - sortKey(b);
  });
}

/**
 * Six rows survive the cap. Review, paused and the one hold a person must
 * clear rarely all stack eight deep on one account at once, so six is enough
 * to hold a whole tier of genuine attention with room to spare, while staying
 * well clear of the wall of thirty-odd identical rows this module exists to
 * end. "Every step →" carries the rest, and the heading's own count already
 * says how many there are in total.
 */
export const STEPS_GLANCE_LIMIT = 6;

/** The ranked list, cut to `limit` (`STEPS_GLANCE_LIMIT` by default). */
export function stepsForGlance(tasks: readonly Task[], limit = STEPS_GLANCE_LIMIT): Task[] {
  return rankStepsForGlance(tasks).slice(0, limit);
}
